import React from 'react';
import {
  AddUniversal,
  RefreshUniversal,
  SubtractUniversal,
} from '@duxton-web/icons';
import {
  Collapse,
  CollapseProps,
  Divider,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';

import ChangelogTargetDiff from '../ChangelogTargetDiff';
import { ChangelogState } from '@/common/const';
import { ModifiedRolloutTargetMapValue } from '@/common/dto/changelog';
import { VariableType } from '@/constants';
import { useVariable } from '@/contexts/VariableContext';
import JsonDiff from '@/features/changelog/json-diff';
import { VariableInfo } from '@/lib/PlannerDTOs';
import { getRolloutTargetIndex, getTitle } from '@/utils/changelog';
import { analyzeSingleRolloutTarget } from '@/utils/rolloutImpactAnalysis';

interface OwnProps {
  'data-testid'?: string;
  rolloutTargets: Map<string, ModifiedRolloutTargetMapValue>;
  changelog?: any;
  variable?: VariableInfo;
}

function StateIcon({ state }: { state: string }) {
  switch (state) {
    case ChangelogState.ADDED:
      return (
        <div
          data-testid="rollout-diff-add-icon"
          className="changelog-item-added diff-panel-state-icon"
        >
          <AddUniversal />
        </div>
      );
    case ChangelogState.DELETED:
      return (
        <div
          data-testid="rollout-diff-subtract-icon"
          className="changelog-item-deleted diff-panel-state-icon"
        >
          <SubtractUniversal />
        </div>
      );
    case ChangelogState.MODIFIED:
      return (
        <div
          data-testid="rollout-diff-refresh-icon"
          className="changelog-item-modified diff-panel-state-icon"
        >
          <RefreshUniversal />
        </div>
      );
    default:
      return <></>;
  }
}

function GlobalImpactIndicator() {
  return (
    <Tooltip title="This rollout target has potential GLOBAL impact affecting all countries">
      <Tag
        color="red"
        style={{
          fontWeight: 700,
          fontSize: 10,
          padding: '0px 6px',
          borderRadius: 4,
        }}
      >
        GLOBAL
      </Tag>
    </Tooltip>
  );
}

function RolloutTargetPanelTitle({
  value,
  isGlobalImpact,
}: {
  value: ModifiedRolloutTargetMapValue;
  isGlobalImpact: boolean;
}) {
  return (
    <div className="diff-panel-title-wrapper">
      <StateIcon state={value.state} />
      <div className="diff-panel-title">
        <Tooltip title={getTitle(value)}>
          <Typography.Text ellipsis>{getTitle(value)}</Typography.Text>
        </Tooltip>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {isGlobalImpact && <GlobalImpactIndicator />}
          {getRolloutTargetIndex(value) > 0 && (
            <Tag style={{ minWidth: 28, textAlign: 'center' }}>
              <Typography.Metadata
                style={{
                  color: '#3D3D3D',
                }}
                strong
              >
                {getRolloutTargetIndex(value)}
              </Typography.Metadata>
            </Tag>
          )}
          <Divider type="vertical" />
        </div>
      </div>
    </div>
  );
}

interface RolloutDiffContentProps {
  value: ModifiedRolloutTargetMapValue;
  changelog: any;
}

const RolloutDiffContent = ({ value, changelog }: RolloutDiffContentProps) => {
  const variable = useVariable();
  let type: VariableType;

  if (!variable) {
    type = changelog?.type as VariableType;
  } else {
    type = variable.type as VariableType;
  }

  const showJsonDiff = [VariableType.JSON, VariableType.MAP].includes(type);

  if (showJsonDiff) {
    return <JsonDiff value={value} changelog={changelog} variable={variable} />;
  }

  return (
    <ChangelogTargetDiff
      value={value}
      changelog={changelog}
      variable={variable}
    />
  );
};

export default function RolloutDiff({
  'data-testid': dataTestId,
  rolloutTargets,
  changelog,
}: OwnProps) {
  const items: CollapseProps['items'] = [];

  rolloutTargets.forEach((value: ModifiedRolloutTargetMapValue) => {
    const impact = analyzeSingleRolloutTarget(value);
    const isGlobal = impact.isGlobal;

    items.push({
      label: (
        <RolloutTargetPanelTitle value={value} isGlobalImpact={isGlobal} />
      ),
      children: <RolloutDiffContent value={value} changelog={changelog} />,
      className: isGlobal ? 'global-rollout-target' : undefined,
    });
  });

  return (
    <Collapse
      accordion
      data-testid={dataTestId}
      className="changelog-item-rollout-targets-container"
      items={items}
      expandIconPosition="end"
    />
  );
}
